import { useCallback, useEffect, useRef, useState } from 'react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import {
  createOperatingCostCategory,
  createOperatingCostEntry,
  createOperatingCostMonthlyEntry,
  deleteOperatingCostCategory,
  deleteOperatingCostEntry,
  deleteOperatingCostMonthlyEntry,
  fetchOperatingCosts,
  renameOperatingCostCategory,
  updateOperatingCostEntry,
  updateOperatingCostMonthlyEntry,
  updateOperatingCostUnitRate,
  type CreateOperatingCostCategoryInput,
  type CreateOperatingCostEntryInput,
  type CreateOperatingCostMonthlyEntryInput,
} from '@/lib/api/operating-costs-admin.service'
import type { OperatingCostsSnapshot } from '@/types/api'
import {
  OPERATING_COSTS_CATEGORY_CREATED,
  OPERATING_COSTS_CATEGORY_DELETED,
  OPERATING_COSTS_CATEGORY_UPDATED,
  OPERATING_COSTS_ENTRY_CREATED,
  OPERATING_COSTS_ENTRY_DELETED,
  OPERATING_COSTS_ENTRY_UPDATED,
  OPERATING_COSTS_LOAD_ERROR,
  OPERATING_COSTS_MONTHLY_ENTRY_CREATED,
  OPERATING_COSTS_MONTHLY_ENTRY_DELETED,
  OPERATING_COSTS_MONTHLY_ENTRY_UPDATED,
  OPERATING_COSTS_UNIT_RATE_UPDATED,
  resolveOperatingCostsError,
  resolveOperatingCostsSnapshot,
} from '../lib/operating-costs'
import { operatingCostMonthCount } from '../lib/entry-months'

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true
  if (
    error instanceof Error &&
    (error.name === 'AbortError' || /abort/i.test(error.message))
  ) {
    return true
  }
  return false
}

export type OperatingCostsController = {
  snapshot: OperatingCostsSnapshot | null
  isLoading: boolean
  isSaving: boolean
  error: string | null
  success: string | null
  createCategory: (input: CreateOperatingCostCategoryInput) => Promise<boolean>
  renameCategory: (categoryId: number, name: string) => Promise<boolean>
  deleteCategory: (categoryId: number) => Promise<boolean>
  createEntry: (
    categoryId: number,
    input: CreateOperatingCostEntryInput
  ) => Promise<boolean>
  updateEntry: (entryId: number, input: CreateOperatingCostEntryInput) => Promise<boolean>
  deleteEntry: (entryId: number) => Promise<boolean>
  createMonthlyEntry: (
    categoryId: number,
    input: CreateOperatingCostMonthlyEntryInput
  ) => Promise<boolean>
  updateMonthlyEntry: (
    entryId: number,
    input: CreateOperatingCostMonthlyEntryInput
  ) => Promise<boolean>
  deleteMonthlyEntry: (entryId: number) => Promise<boolean>
  updateUnitRate: (categoryId: number, amount: number) => Promise<boolean>
  reload: () => Promise<void>
}

export function useOperatingCosts(): OperatingCostsController {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const runWithStepUpRef = useRef(runWithStepUp)
  runWithStepUpRef.current = runWithStepUp
  const [snapshot, setSnapshot] = useState<OperatingCostsSnapshot | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const loadControllerRef = useRef<AbortController | null>(null)
  const isSavingRef = useRef(false)

  const loadSnapshot = useCallback(async (silent = false) => {
    loadControllerRef.current?.abort()
    const controller = new AbortController()
    loadControllerRef.current = controller
    if (!silent) {
      setIsLoading(true)
    }
    setError(null)
    try {
      const response = await runWithStepUpRef.current(() =>
        fetchOperatingCosts(controller.signal)
      )
      if (controller.signal.aborted) return
      const resolved = resolveOperatingCostsSnapshot(response)
      if (!resolved) {
        setSnapshot(null)
        setError(OPERATING_COSTS_LOAD_ERROR)
        return
      }
      setSnapshot(resolved)
    } catch (err) {
      if (controller.signal.aborted || isAbortError(err)) return
      setSnapshot(null)
      setError(resolveOperatingCostsError(err))
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    void loadSnapshot()
    return () => {
      loadControllerRef.current?.abort()
    }
  }, [loadSnapshot])

  const runMutation = useCallback(
    async (action: () => Promise<unknown>, successMessage: string): Promise<boolean> => {
      if (isSavingRef.current) {
        return false
      }
      isSavingRef.current = true
      setIsSaving(true)
      setError(null)
      setSuccess(null)
      try {
        await runWithStepUpRef.current(action)
        setSuccess(successMessage)
        await loadSnapshot(true)
        return true
      } catch (err) {
        if (isAbortError(err)) return false
        setError(resolveOperatingCostsError(err))
        return false
      } finally {
        isSavingRef.current = false
        setIsSaving(false)
      }
    },
    [loadSnapshot]
  )

  return {
    snapshot,
    isLoading,
    isSaving,
    error,
    success,
    createCategory: (input) =>
      runMutation(
        () => createOperatingCostCategory(input),
        OPERATING_COSTS_CATEGORY_CREATED
      ),
    renameCategory: (categoryId, name) =>
      runMutation(
        () => renameOperatingCostCategory(categoryId, name),
        OPERATING_COSTS_CATEGORY_UPDATED
      ),
    deleteCategory: (categoryId) =>
      runMutation(
        () => deleteOperatingCostCategory(categoryId),
        OPERATING_COSTS_CATEGORY_DELETED
      ),
    createEntry: (categoryId, input) =>
      runMutation(
        () => createOperatingCostEntry(categoryId, input),
        input.competenceMonth && input.competenceMonthEnd
          ? `${operatingCostMonthCount(input.competenceMonth, input.competenceMonthEnd)} lançamentos criados. Cada mês pode ser editado ou excluído separadamente.`
          : OPERATING_COSTS_ENTRY_CREATED
      ),
    updateEntry: (entryId, input) =>
      runMutation(
        () => updateOperatingCostEntry(entryId, input),
        OPERATING_COSTS_ENTRY_UPDATED
      ),
    deleteEntry: (entryId) =>
      runMutation(() => deleteOperatingCostEntry(entryId), OPERATING_COSTS_ENTRY_DELETED),
    createMonthlyEntry: (categoryId, input) =>
      runMutation(
        () => createOperatingCostMonthlyEntry(categoryId, input),
        OPERATING_COSTS_MONTHLY_ENTRY_CREATED
      ),
    updateMonthlyEntry: (entryId, input) =>
      runMutation(
        () => updateOperatingCostMonthlyEntry(entryId, input),
        OPERATING_COSTS_MONTHLY_ENTRY_UPDATED
      ),
    deleteMonthlyEntry: (entryId) =>
      runMutation(
        () => deleteOperatingCostMonthlyEntry(entryId),
        OPERATING_COSTS_MONTHLY_ENTRY_DELETED
      ),
    updateUnitRate: (categoryId, amount) =>
      runMutation(
        () => updateOperatingCostUnitRate(categoryId, amount),
        OPERATING_COSTS_UNIT_RATE_UPDATED
      ),
    reload: () => loadSnapshot(true),
  }
}
